import { Component, Input, EventEmitter, Output, OnInit, Inject, Optional, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DataBagService } from '@/common/data-bag.service';
import { ReservationSummaryStoreService } from '../../reservation-summary/services/reservation-summary-store.service';
import { ReservationSummaryApiService } from '../../reservation-summary/services/reservation-summary-api.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { AddedAttachment, Conversation, DocumentToSend, UserData } from '../../reservation.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, filter, map, startWith, Subscription, take } from 'rxjs';
import { ReservationService } from '../../reservation.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { ReservationSummaryDocumentsComponent } from '../../reservation-summary/reservation-summary-documents/reservation-summary-documents.component';
import { DateService } from '@/common/date.service';
import { LoaderService } from '@/common/loader/loader.service';
import { PricePipe } from '@/common/pipes/price.pipe';
import { DownloadFileService } from '@/common/download-file.service';
import { DatePipe } from '@angular/common';
import { FileSizeUnit } from '@/common/enums/FileSizeUnit';
import { DocumentDownloader } from '../../reservation-summary/reservation-summary-documents/document-downloader';
import { CancellationStatuses, Status } from '@/common/enums/Status';

export interface FileEventDto {
	target: {
		files: Blob[];
	};
}
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { RequestTrackerInterceptor } from '../../../common/interceptors/request-tracker.interceptor';
import { RequestTrackerService } from '../../../common/request-tracker.service';
import { DateBoundsPipe } from '../../../common/pipes/date-bounds.pipe';
import { User } from '@/tasks/task.model';
import { EmailType } from '@/common/enums/EmailType';
import { DocumentName } from '@/common/enums/DocumentName';

@Component({
	selector: 'app-reservation-message-send',
	templateUrl: './reservation-message-send.component.html',
	styleUrls: ['./reservation-message-send.component.scss'],
	providers: [
		PricePipe,
		DatePipe,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: RequestTrackerInterceptor,
			multi: true,
		},
	],
})
export class ReservationMessageSendComponent extends ReservationSummaryDocumentsComponent implements OnInit, OnDestroy {
	constructor(
		private readonly bag: DataBagService,
		public store: ReservationSummaryStoreService,
		protected infobox: InfoboxService,
		@Optional()
		@Inject(MAT_DIALOG_DATA)
		public resId: number,
		@Optional()
		public dialogRef: MatDialogRef<ReservationMessageSendComponent>,
		private readonly formBuilder: FormBuilder,
		public _service: ReservationService,
		protected api: DataBagService,
		protected dateService: DateService,
		protected reservationApiService: ReservationSummaryApiService,
		protected loader: LoaderService,
		protected pricePipe: PricePipe,
		private readonly downloadService: DownloadFileService,
		protected datePipe: DatePipe,
		protected documentDownloader: DocumentDownloader,
		protected requestTracker: RequestTrackerService,
		protected dateBoundsPipe: DateBoundsPipe,
	) {
		super(
			_service,
			api,
			dateService,
			store,
			infobox,
			reservationApiService,
			loader,
			pricePipe,
			datePipe,
			documentDownloader,
			requestTracker,
			dateBoundsPipe,
		);
	}

	@Input() conversationId: string = null;
	@Input() lastMessageId: string;
	@Input() isAnswer = false;
	@Output() closeAnswer = new EventEmitter();
	formGroup: FormGroup;
	loading = false;
	protected userLoading = false;
	filteredUsers: UserData[];
	private htmlTemplate = '';
	protected attachments: AddedAttachment[] = [];
	protected data: Conversation;
	protected loggedUser: User;
	protected sellerMail = '';
	private selectedAddressee = '';
	private userSubscription: Subscription;
	private reservationSubscription: Subscription;
	private assignAllParticipantsSubscription: Subscription;

	editorConfig: AngularEditorConfig = {
		editable: true,
		spellcheck: true,
		height: 'auto',
		minHeight: '300px',
		maxHeight: '400px',
		width: 'auto',
		minWidth: '0',
		translate: 'yes',
		enableToolbar: true,
		showToolbar: true,
		placeholder: 'Napisz wiadomość...',
		defaultParagraphSeparator: '',
		defaultFontName: '',
		defaultFontSize: '',
		fonts: [
			{ class: 'arial', name: 'Arial' },
			{ class: 'times-new-roman', name: 'Times New Roman' },
			{ class: 'calibri', name: 'Calibri' },
			{ class: 'comic-sans-ms', name: 'Comic Sans MS' },
		],

		sanitize: false,
		toolbarPosition: 'top',
		toolbarHiddenButtons: [['insertImage', 'insertVideo']],
	};
	protected attachmentMaxSize = 20000000;
	protected attachmentSize = 0;

	ngOnInit() {
		super.ngOnInit();
		const { loggedUser, reservation } = this.store.getters();
		this.userSubscription = loggedUser()
			.pipe(filter((user) => !!user))
			.subscribe((user) => (this.loggedUser = user));

		if (this.conversationId) {
			this.reservationApiService
				.actions()
				.getConversation(this.conversationId)
				.then((value) => {
					this.data = value;
				});
		}
		if (this.isAnswer) this.editorConfig.maxHeight = '300px';
		this.formGroup = this.formBuilder.group({
			addressee: new FormControl('', !this.isAnswer ? [Validators.required, this.validateEmailList] : []),
			subject: new FormControl('', !this.isAnswer ? [Validators.required] : []),
			message: new FormControl('', [Validators.required]),
			assignAllParticipants: new FormControl(false),
			sellerSend: new FormControl(!!this.sellerMail),
			template: new FormControl(''),
		});
		this.reservationSubscription = this._service.reservation$
			.pipe(
				filter((reservation) => {
					return !!reservation.seller && !!reservation.ownerUser;
				}),
			)
			.subscribe((value) => {
				this.sellerMail = value.seller.departments.find(
					(department) => department.departmentContacts[0].email,
				)?.departmentContacts[0].email;
				this.selectedAddressee = value.ownerUser.userContacts[0].email;
				this.updateFormValue();
			});
		this._service.loadUsers();
		this.filteredUsers = this.filterUsersByMail(this._service.users);
		this.formGroup
			.get('addressee')
			.valueChanges.pipe(
				startWith(''),
				debounceTime(400),
				map((value) => this._filterUsers(value || '')),
			)
			.subscribe((filtered) => {
				this.filteredUsers = filtered;
			});

		this.assignAllParticipantsSubscription = this.formGroup
			.get('assignAllParticipants')
			.valueChanges.subscribe((value) => {
				if (!value) {
					this._service.reservation$.pipe(take(1)).subscribe((reservation) => {
						this.formGroup.get('addressee').setValue(reservation.ownerUser.userContacts[0].email);
					});
				} else {
					const addressee: string[] = this.formGroup
						.get('addressee')
						.value.split(',')
						.map((element: string) => element.trim());

					const participants = this._service.reservation$
						.getValue()
						.offer.filter((offer) => {
							return !CancellationStatuses.includes(offer.status as Status);
						})
						.flatMap((offer) =>
							offer.slot
								.filter((slot) => {
									return (
										!CancellationStatuses.includes(slot.status as Status) &&
										!!slot.userData &&
										slot.userData.userContacts.length &&
										slot.userData.userContacts.some((contact) => !!contact.email)
									);
								})
								.flatMap((slot) => slot.userData.userContacts[0].email),
						);

					participants.push(this.reservation.ownerUser.userContacts[0].email);

					const withoutDuplicates = new Set([...addressee, ...participants]);

					const emails = [...withoutDuplicates].join(',');

					this.formGroup.get('addressee').setValue(emails);
				}
			});
	}

	get documentForMail() {
		return this._service.documentKinds.filter(
			(value) => value.displayName.includes('MAIL') && value.name !== 'reservation_cash_confirmation',
		);
	}

	private validateEmailList(control: AbstractControl): { [key: string]: any } | null {
		const emailList = control.value instanceof Array ? control.value.join(',') : control.value;

		const emails = emailList.split(',').map((email: string) => email.trim());
		const pattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
		const allValid = emails.every((email: string) => pattern.test(email));
		return allValid ? null : { emailInvalid: true };
	}

	private filterUsersByMail(users: UserData[]) {
		return users.filter((v) => {
			return v.userContacts.find((contact) => contact.email);
		});
	}

	protected setAddresseeFromInput($event: Event) {
		const inputElement = $event.target as HTMLInputElement;
		if (inputElement instanceof HTMLInputElement) {
			this.selectedAddressee = inputElement.value;
			this.updateFormValue();
		}
	}

	private updateFormValue() {
		this.formGroup.get('addressee')?.setValue(this.selectedAddressee);
	}

	selectUser($event: MatAutocompleteSelectedEvent) {
		const selectedEmail = $event.option.value;
		const parts = this.selectedAddressee.split(',').slice(0, -1);
		parts.push(` ${selectedEmail}`);
		this.selectedAddressee = parts.join(',');
		this.updateFormValue();
	}

	private _filterUsers(value: string): UserData[] {
		if (value && typeof value === 'string') {
			if (value.length < 3) return this.filterUsersByMail(this._service.users);
			this.userLoading = true;
			this.bag.get(`/profile/data_full/?full_name=${value.split(',').pop()}`, (result, error) => {
				this.filteredUsers = this.filterUsersByMail(result ? result.results : this._service.users);
				this.userLoading = false;
			});
			return;
		}
		return this.filterUsersByMail(this._service.users);
	}

	private b64EncodeUnicode(str) {
		return btoa(
			encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
				return String.fromCharCode(parseInt(p1, 16));
			}),
		);
	}

	private removeHeadTags(htmlString: string): string {
		const combinedRegex =
			/<style\b[^>]*>[\s\S]*?<\/style>|<meta\b[^>]*>|<\/meta>|<title\b[^>]*>[\s\S]*?<\/title>|<link\b[^>]*>|<\/link>|<base\b[^>]*>|<\/base>|<script\b[^>]*>[\s\S]*?<\/script>/gi;
		return htmlString.replace(combinedRegex, '');
	}

	private replaceTemplateBodyByMessage(): string {
		const regex = /<body\b[^>]*>([\s\S]*?)<\/body>/;
		const messageBodyString = this.removeHeadTags(this.formGroup.get('message').value);
		return this.htmlTemplate.replace(regex, messageBodyString);
	}

	protected selectTemplate() {
		this.api.post(
			'/document/preview/',
			{
				name: this.formGroup.get('template').value,
				version: 'v1',
				params: this.setParams(this.formGroup.get('template').value),
			},
			async (result, error) => {
				if (result) {
					const templateName = {
						name: this.formGroup.get('template').value,
					};

					const formattedTemplateName = this._service
						.convertDocumentName(templateName)
						.split('MAIL: ')
						.join('');

					const newSubject = `${formattedTemplateName} #${this.reservation.id} ${this.reservation.offer[0].product.name}`;
					if (
						result.name === EmailType.RESERVATION_CONFIRM_MAIL ||
						result.name === EmailType.RESERVATION_CONFIRM_MAIL_FOR_AGENT
					) {
						const reservationAgreementDocumentId = await this.createDocument(
							DocumentName.RESERVATION_AGREEMENT,
						);
						if (reservationAgreementDocumentId)
							await this.generatePdf(reservationAgreementDocumentId, 'umowa');
					}

					if (result.name === EmailType.RESERVATION_CONFIRM_MAIL_FOR_AGENT) {
						const reservationConfirmDocumentId = await this.createDocument(
							DocumentName.RESERVATION_CONFIRM,
						);
						if (reservationConfirmDocumentId)
							await this.generatePdf(reservationConfirmDocumentId, `Rezerwacja nr ${this.resId}`);
					}

					this.htmlTemplate = result.html;
					this.formGroup.get('message').setValue(result.html);
					this.formGroup.get('subject').setValue(newSubject);
				} else {
					throw new Error(JSON.stringify(error.error));
				}
			},
		);
	}

	async generatePdf(documentId: number, name: string): Promise<any> {
		const pdfBlob = await this.documentDownloader.fetchDocument(documentId);
		const file = new File([pdfBlob as BlobPart], `${name}.pdf`, {
			type: 'application/pdf',
		});

		this.loader.hide('creating-agreement');
		const fakeFileEvent: FileEventDto = {
			target: {
				files: [file],
			},
		};

		await this.addAttachments(fakeFileEvent);
	}

	async createDocument(documentName: string): Promise<number> {
		this.loader.show('creating-document');
		return new Promise((resolve, reject) => {
			const document = this._service.documentKinds.find((document) => document.name === documentName)!;
			const documentToSend: DocumentToSend = {
				name: document.name,
				version: document.version,
				params: this.setParams(documentName),
				reservations: [this.reservation.id],
			};
			this.api.post(`/reservation/${this.reservation.id}/documents/`, documentToSend, async (result, error) => {
				if (result) {
					resolve(result.id);
				} else {
					this.loader.hide('creating-document');
					reject();
				}
			});
		});
	}

	checkSend() {
		if (this.data) this.sendAnswer();
		else this.createThread();
	}

	private createThread() {
		const addressee: string[] = this.formGroup
			.get('addressee')
			.value.split(',')
			.map((element: string) => element.trim());

		if (this.formGroup.get('sellerSend').value) {
			addressee.push(this.sellerMail);
		}

		this.loading = true;
		const thread = {
			addressee: addressee,
			subject: this.formGroup.get('subject').value,
			body: this.b64EncodeUnicode(
				this.formGroup.get('template').value
					? this.replaceTemplateBodyByMessage()
					: this.formGroup.get('message').value,
			),
			attachments: this.attachments,
		};
		this.bag.post(`/reservation/${this.resId}/conversation/`, thread, (result, error) => {
			if (error) {
				this.loader.hide();
				return;
			}
			if (result) this.dialogRef.close();
			this.loading = false;
		});
	}

	private sendAnswer() {
		const answer = {
			id: this.lastMessageId,
			body: this.b64EncodeUnicode(this.formGroup.get('message').value),
			attachments: this.attachments,
		};
		this.store.mutations().setMessages([]);
		this.bag.post(`/conversation/threads/${this.data.id}/messages/`, answer, (result, error) => {
			if (error) {
				return;
			}
			setTimeout(() => this.reservationApiService.actions().getMessages(this.data.id), 3000);
		});
	}

	protected removeAttachment(i: number) {
		this.attachments.splice(i, 1);
	}

	protected downloadAddedAttachment(fileObject: AddedAttachment) {
		const modifiedFileObject = Object.assign({}, fileObject, {
			base64Url: fileObject['content'],
		});
		delete modifiedFileObject['content'];
		this.downloadService.downloadFile(modifiedFileObject);
	}

	protected async addAttachments(event: Event | FileEventDto): Promise<void> {
		const inputElement = event.target as HTMLInputElement;
		if (!inputElement.files.length) {
			return;
		}
		const attachmentPromises: Promise<AddedAttachment>[] = [];
		Array.from(inputElement.files).forEach((file) => {
			const promise = this.readFileAsAttachmentObject(file);
			attachmentPromises.push(promise);
		});
		await Promise.all(attachmentPromises).then((results) => {
			if (this.attachmentMaxSize < this.attachmentSize) {
				this.attachmentSize = results.reduce((accumulator, value) => {
					return accumulator - value.size;
				}, this.attachmentSize);
				return this.infobox.warning(
					`Przekroczono dopuszczalny rozmiar załączników. <br> Łączna maksymalna wielkość plików to ${
						this.attachmentMaxSize / 1000000
					}mb`,
				);
			}

			this.attachments = this.attachments.concat(results);
		});
	}

	private readFileAsAttachmentObject(file: File): Promise<AddedAttachment> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				if (reader.result) {
					const base64Content = btoa(reader.result as string);
					this.attachmentSize += file.size;
					resolve({
						filename: file.name,
						mimeType: file.type,
						content: base64Content,
						size: file.size,
					});
				} else {
					reject(new Error('Failed to read file as base64.'));
				}
			};
			reader.onerror = (error) => {
				reject(error);
			};

			reader.readAsBinaryString(file);
		});
	}

	ngOnDestroy() {
		this.userSubscription.unsubscribe();
		this.reservationSubscription.unsubscribe();
		this.assignAllParticipantsSubscription.unsubscribe();
	}

	protected readonly FileSizeUnit = FileSizeUnit;
}
